import React, { useCallback, useEffect } from "react";
import {
  getSafeNumericValue,
  isInProgressDecimal,
  isValidDecimalInput,
  sanitizeNumericInput,
} from "../../../utils";
import InputWithIcon from "../../commons/InputWithIcon";
import CustomSlider from "../../commons/CustomSlider";
import { useBot } from "../../../context/botContext";
import LabelWithTooltip from "../../commons/LabelWithTooltip";

const XenoarcV2Setting = ({
  setFormXenoV2,
  formXenoV2,
  baseCapital,
  errors,
  selectedPair,
  initialBotTradeSize,
  additionalTradeSize,
  setAdditionalTradeSize,
}) => {
  // Derive state directly from props for a single source of truth
  const {
    capital,
    amount = 0,
    number_of_grids = 0,
    grid_width = 0,
    grid_width_exit = 0,
    buy_flg_return = 0,
    exit_flg_return = 0,
  } = formXenoV2.settings;

  const { openDialogBot } = useBot();

  // Memoize the update function to ensure stability
  const updateSettings = useCallback(
    (key, value) => {
      setFormXenoV2((prev) => ({
        ...prev,
        settings: {
          ...prev.settings,
          [key]: value,
        },
      }));
    },
    [setFormXenoV2],
  );

  // EFFECT: Adjust capital if baseCapital changes and becomes smaller
  // This ensures the allocated capital never exceeds the available baseCapital.
  // useEffect(() => {
  //   const numericCapital = getSafeNumericValue(capital || "0");
  //   const numericTradeSize = getSafeNumericValue(amount || "0");

  //   // If the current trade size is greater than the total allocated capital,
  //   // reduce the trade size to match the capital.
  //   if (numericTradeSize > numericCapital) {
  //     updateSettings("amount", numericCapital.toString());
  //   }
  // }, [amount, capital, updateSettings]); // This runs whenever capital or trade size changes

  // Handler for capital input field
  const handleCapitalChange = (e) => {
    const inputValue = e.target.value;

    if (!isValidDecimalInput(inputValue)) return;

    // Always update the input field with raw user input
    updateSettings("capital", inputValue);

    // Only do logic when input is a complete number (not in-progress like "13.")
    if (!isInProgressDecimal(inputValue) && inputValue !== "") {
      const numericCapital = getSafeNumericValue(inputValue);

      // Ensure amount does not exceed new capital
      const currentAmount = getSafeNumericValue(
        formXenoV2.settings.amount || "0",
      );

      if (currentAmount > numericCapital) {
        updateSettings("amount", numericCapital.toString());
      }
    }
  };

  // Handler for the percentage slider
  const handleSliderChange = (newPercentage) => {
    const newCapital = parseFloat(
      ((newPercentage / 100) * baseCapital).toFixed(2),
    );
    const newCapitalStr = newCapital.toString();

    updateSettings("capital", newCapitalStr); // use string for consistent input rendering

    // Clamp amount if needed
    const currentAmount = getSafeNumericValue(
      formXenoV2.settings.amount || "0",
    );
    if (currentAmount > newCapital) {
      updateSettings("amount", newCapitalStr);
    }
  };
  // Generic handler for other numeric setting fields
  const handleSettingChange = (key, rawValue) => {
    // const max = key === "amount" ? capital : Infinity;
    // if (isInProgressDecimal(value)) {
    //   updateSettings(key, value);
    // } else {
    //   const numericValue = getSafeNumericValue(value, max);
    //   updateSettings(key, numericValue);
    // }
    const value = sanitizeNumericInput(rawValue, 8, false);
    if (isEdit && key === "amount") {
      setAdditionalTradeSize(value);
    }
    updateSettings(key, value);
  };

  // const handleSettingBlur = (key, value) => {
  //   if (key === "amount") {
  //     let numericValue = getSafeNumericValue(value);
  //     const min = 5;
  //     const max = getSafeNumericValue(amount);

  //     if (numericValue < min) numericValue = min;
  //     if (numericValue > max) numericValue = max;

  //     updateSettings(key, numericValue.toString());
  //   }
  // };
  // Calculate percentage for the slider, preventing division by zero
  const percentage = baseCapital > 0 ? (capital / baseCapital) * 100 : 0;

  const isEdit = openDialogBot === "step-bot-2-edit";

  // Data structure for rendering input fields to keep JSX clean
  const inputFields = [
    {
      id: "amount",
      label: isEdit
        ? `<strong>New</strong> Trade Size <span class="text-gray-400 text-xs">(current: ${initialBotTradeSize})</span>`
        : "Trade Size",
      placeholder: "Trade Size",
      icon: selectedPair?.base_currency || "USDT",
      // value: amount,
      value: amount,
      // value: isEdit ? additionalTradeSize : amount,
      explanation: isEdit
        ? `Current trade size is ${initialBotTradeSize}. Enter additional amount to add.`
        : "Total capital the bot can use.",
    },
    {
      id: "number_of_grids",
      label: "Number of step",
      placeholder: "Number of step",
      icon: "levels",
      value: number_of_grids,
      explanation: "Number of price levels in the grid.",
    },
    {
      id: "grid_width",
      label: "<strong>Buy-step</strong> width",
      placeholder: "Buy-step width",
      icon: "%",
      value: grid_width,
      explanation: "Spacing between buy orders.",
    },
    {
      id: "grid_width_exit",
      label: "<strong>Sell-step</strong> width",
      placeholder: "Sell-step width",
      icon: "%",
      value: grid_width_exit,
      explanation: "Spacing between sell orders.",
    },
    {
      id: "buy_flg_return",
      label: "<strong>Buy-flag</strong> return",
      placeholder: "Buy-flag return",
      icon: "%",
      value: buy_flg_return,
      explanation: "Extra confirmation before buying.",
    },
    {
      id: "exit_flg_return",
      label: "<strong>Exit-flag</strong> return",
      placeholder: "Exit-flag return",
      icon: "%",
      value: exit_flg_return,
      explanation: "Extra confirmation before selling.",
    },
  ];

  return (
    <>
      {/* <InputWithIcon
        value={capital}
        onChange={handleCapitalChange}
        placeholder="Capital Allocation"
        rightIcon="USDT"
        inputClassName="text-right"
      />
      <CustomSlider
        percentage={percentage}
        setPercentage={handleSliderChange}
      /> */}

      <div className="grid grid-cols-2 gap-4">
        {inputFields.map((field) => {
          // Get the specific error for the current field's id
          const fieldError = errors[field.id];

          return (
            <div key={field.id}>
              <LabelWithTooltip
                label={field.label}
                explanation={field.explanation}
              />
              <InputWithIcon
                id={field.id}
                value={field.value}
                onChange={(e) => handleSettingChange(field.id, e.target.value)}
                placeholder={field.placeholder}
                rightIcon={field.icon}
                // Pass an isError prop to style the input if there's an error
                isError={!!fieldError}
                disabled={!!isEdit && field.id !== "amount"}
              />
              {/* Conditionally render the error message below the input */}
              {fieldError && (
                <p className="text-red-500 text-sm mt-1.5">{fieldError}</p>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
};

export default XenoarcV2Setting;
